import React, { useRef, useState } from "react";
import Navbar from "../components/jobsiq/Navbar";
import Footer from "../components/jobsiq/Footer";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { FloppyDisk, Sparkle, ArrowSquareOut } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import ResumeParseDialog from "../components/jobsiq/ResumeParseDialog";

export default function Profile() {
    const { user, setUser } = useAuth();
    const [form, setForm] = useState({
        name: user?.name || "",
        title: user?.title || "",
        bio: user?.bio || "",
        location: user?.location || "",
        experience_years: user?.experience_years ?? 0,
        skills: (user?.skills || []).join(", "),
        company_name: user?.company_name || "",
    });
    const [saving, setSaving] = useState(false);
    const [parsing, setParsing] = useState(false);
    const [parseResult, setParseResult] = useState(null);
    const fileRef = useRef(null);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...form,
                skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
                experience_years: Number(form.experience_years) || 0,
            };
            const { data } = await api.put("/auth/profile", payload);
            setUser(data);
            toast.success("Profile saved");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const onParseFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setParsing(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/profile/parse-resume", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setParseResult(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Parse failed");
        } finally {
            setParsing(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const applyParsed = async (fields) => {
        try {
            const { data } = await api.put("/auth/profile", fields);
            setUser(data);
            setForm({
                name: data.name || "",
                title: data.title || "",
                bio: data.bio || "",
                location: data.location || "",
                experience_years: data.experience_years ?? 0,
                skills: (data.skills || []).join(", "),
                company_name: data.company_name || "",
            });
            setParseResult(null);
            toast.success("Profile updated from résumé");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Apply failed");
        }
    };

    return (
        <div data-testid="profile-page">
            <Navbar />
            <section className="max-w-[900px] mx-auto px-6 py-10">
                <div className="font-mono-plex text-xs uppercase tracking-[0.3em] text-blue-600 mb-2">
                    Profile
                </div>
                <h1 className="font-display text-4xl">Sharpen your talent intelligence.</h1>
                <p className="text-slate-600 mt-2">
                    What you write here shapes how the matching model reads you.
                </p>

                {user?.role === "seeker" && (
                    <div className="mt-6 border border-slate-200 p-4 flex items-center justify-between gap-4 flex-wrap bg-slate-50">
                        <div>
                            <div className="font-mono-plex text-xs uppercase tracking-[0.3em] text-blue-600 mb-1 inline-flex items-center gap-1.5">
                                <Sparkle size={12} weight="fill" /> AI résumé parser
                            </div>
                            <div className="text-sm text-slate-700">
                                Upload a PDF résumé and we'll extract your skills, title, experience and bio. You decide what gets applied.
                            </div>
                        </div>
                        <label className="cursor-pointer px-4 py-2 bg-slate-950 text-white hover:bg-blue-600 inline-flex items-center gap-1.5 text-sm">
                            <Sparkle size={14} weight="fill" />
                            {parsing ? "Parsing…" : "Parse résumé with AI"}
                            <input
                                ref={fileRef}
                                hidden
                                type="file"
                                accept=".pdf,application/pdf"
                                onChange={onParseFile}
                                data-testid="parse-resume-input"
                                disabled={parsing}
                            />
                        </label>
                        {user?.user_id && (
                            <Link
                                to={`/p/${user.user_id}`}
                                target="_blank"
                                rel="noopener"
                                data-testid="view-public-profile"
                                className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                            >
                                <ArrowSquareOut size={14} /> View public profile
                            </Link>
                        )}
                    </div>
                )}

                <form onSubmit={save} className="mt-8 space-y-5" data-testid="profile-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-mono-plex uppercase tracking-widest text-slate-500 mb-1">Name</label>
                            <input data-testid="profile-name" value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none" />
                        </div>
                        <div>
                            <label className="block text-xs font-mono-plex uppercase tracking-widest text-slate-500 mb-1">
                                {user?.role === "company" ? "Company name" : "Headline / title"}
                            </label>
                            <input data-testid="profile-title"
                                value={user?.role === "company" ? form.company_name : form.title}
                                onChange={(e) =>
                                    user?.role === "company"
                                        ? setForm({ ...form, company_name: e.target.value })
                                        : setForm({ ...form, title: e.target.value })
                                }
                                className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none" />
                        </div>
                    </div>

                    {user?.role !== "company" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-mono-plex uppercase tracking-widest text-slate-500 mb-1">Location</label>
                                <input data-testid="profile-location" value={form.location}
                                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                                    className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none" />
                            </div>
                            <div>
                                <label className="block text-xs font-mono-plex uppercase tracking-widest text-slate-500 mb-1">Years of experience</label>
                                <input data-testid="profile-experience" type="number" value={form.experience_years}
                                    onChange={(e) => setForm({ ...form, experience_years: e.target.value })}
                                    className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none" />
                            </div>
                        </div>
                    )}

                    {user?.role !== "company" && (
                        <div>
                            <label className="block text-xs font-mono-plex uppercase tracking-widest text-slate-500 mb-1">
                                Skills (comma-separated)
                            </label>
                            <input data-testid="profile-skills" value={form.skills}
                                onChange={(e) => setForm({ ...form, skills: e.target.value })}
                                placeholder="React, TypeScript, design systems, a11y"
                                className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none" />
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-mono-plex uppercase tracking-widest text-slate-500 mb-1">
                            {user?.role === "company" ? "About the company" : "Bio"}
                        </label>
                        <textarea data-testid="profile-bio" rows={5} value={form.bio}
                            onChange={(e) => setForm({ ...form, bio: e.target.value })}
                            className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none" />
                    </div>

                    <button
                        data-testid="profile-save-btn"
                        disabled={saving}
                        className="bg-slate-950 text-white px-6 py-3 hover:bg-blue-600 disabled:opacity-60 inline-flex items-center gap-2"
                    >
                        <FloppyDisk size={16} /> {saving ? "Saving…" : "Save profile"}
                    </button>
                </form>
            </section>
            <ResumeParseDialog
                open={!!parseResult}
                parsed={parseResult?.parsed}
                current={parseResult?.current}
                onClose={() => setParseResult(null)}
                onApply={applyParsed}
            />
            <Footer />
        </div>
    );
}
